// src/pages/ExcursionPage.js
import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import {
  Container, Typography, Grid, Paper, Box, CircularProgress, Alert, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, Button,
  TextField, InputAdornment, LinearProgress // New imports for fancy UI
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PeopleIcon from '@mui/icons-material/People';
import ParkIcon from '@mui/icons-material/Park';
import MuseumIcon from '@mui/icons-material/Museum';
import DownloadIcon from '@mui/icons-material/Download';

// Helper to assign an icon to an excursion based on its name
const getExcursionIcon = (siteName) => {
  const lowerCaseName = siteName.toLowerCase();
  if (lowerCaseName.includes('park')) return <ParkIcon fontSize="large" />;
  if (lowerCaseName.includes('musium')) return <MuseumIcon fontSize="large" />;
  return <PeopleIcon fontSize="large" />;
};

const ExcursionPage = () => {
  const [summaryData, setSummaryData] = useState([]);
  const [detailData, setDetailData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterId, setFilterId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = async () => {
    try {
      setLoading(true);
      const [summaryRes, detailRes] = await Promise.all([
        api.get('/api/reports/excursion-summary'),
        api.get('/api/reports/excursion-bookings')
      ]);
      setSummaryData(summaryRes.data);
      setDetailData(detailRes.data);
    } catch (err) {
      setError('Failed to load excursion data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredDetails = useMemo(() => {
    let filtered = detailData;

    // First, filter by selected excursion card
    if (filterId !== null) {
      const selectedExcursion = summaryData.find(s => s.ExcursionID === filterId);
      if (selectedExcursion) {
        filtered = filtered.filter(d => d.SiteName === selectedExcursion.SiteName);
      }
    }

    // Then, filter by the search term
    if (searchTerm) {
      const lowerCaseSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(p =>
        p.FullName.toLowerCase().includes(lowerCaseSearch) ||
        p.EmailAddress.toLowerCase().includes(lowerCaseSearch) ||
        p.CountryName?.toLowerCase().includes(lowerCaseSearch)
      );
    }
    return filtered;
  }, [filterId, searchTerm, detailData, summaryData]);

  const totalBookings = useMemo(() => detailData.length, [detailData]);
  
  const handleExport = async () => {
    // ... (This function remains the same as in previous reports)
  };

  return (
    <Container maxWidth="xl">
      <Typography variant="h4" gutterBottom>Excursion Dashboard</Typography>
      {loading && <CircularProgress />}
      {error && <Alert severity="error">{error}</Alert>}

      {!loading && (
        <>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                onClick={() => setFilterId(null)}
                sx={{ p: 2, cursor: 'pointer', border: 2, borderColor: filterId === null ? 'primary.main' : 'transparent', '&:hover': { boxShadow: 6 } }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <PeopleIcon fontSize="large" color="primary" />
                  <Box>
                    <Typography variant="h6">All Bookings</Typography>
                    <Typography variant="h4" component="p" fontWeight="bold">{totalBookings}</Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
            {summaryData.map(summary => {
              const progress = summary.Capacity > 0 ? (summary.BookingCount / summary.Capacity) * 100 : 0;
              return (
              <Grid item xs={12} sm={6} md={3} key={summary.ExcursionID}>
                <Paper
                  onClick={() => setFilterId(summary.ExcursionID)}
                  sx={{ p: 2, cursor: 'pointer', border: 2, borderColor: filterId === summary.ExcursionID ? 'primary.main' : 'transparent', '&:hover': { boxShadow: 6 } }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {getExcursionIcon(summary.SiteName)}
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="h6" noWrap>{summary.SiteName}</Typography>
                      <Typography variant="h4" component="p" fontWeight="bold">
                        {summary.BookingCount}
                        <Typography variant="h6" component="span" color="text.secondary">
                          {summary.Capacity > 0 ? ` / ${summary.Capacity}` : ''}
                        </Typography>
                      </Typography>
                      {summary.Capacity > 0 && <LinearProgress variant="determinate" value={progress} sx={{ mt: 1 }} />}
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            )})}
          </Grid>

          <Paper sx={{p: 2}}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <TextField
                label="Filter participants..."
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>)}}
              />
              <Button onClick={handleExport} variant="outlined" startIcon={<DownloadIcon />}>Export View</Button>
            </Box>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Full Name</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Country</TableCell>
                    <TableCell>Booked Excursion</TableCell>
                    <TableCell>Processed By</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredDetails.map(p => (
                    <TableRow hover key={p.ParticipantID}>
                      <TableCell>{p.FullName}</TableCell>
                      <TableCell><Typography variant="body2">{p.EmailAddress}</Typography><Typography variant="caption">{p.PhoneNumber}</Typography></TableCell>
                      <TableCell>{p.CountryName}</TableCell>
                      <TableCell>{p.SiteName}</TableCell>
                      <TableCell>{p.ProcessedBy}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Container>
  );
};

export default ExcursionPage;